import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import {
  LayoutDashboard,
  Users,
  UserRound,
  GraduationCap,
  UserCheck,
  Building2,
  UserPlus,
  BriefcaseBusiness,
  ClipboardList,
  Search,
  Handshake,
  BookOpen,
  CalendarCheck,
  CheckCircle,
  Bell,
  Megaphone,
  CreditCard,
  Wallet,
  BarChart3,
  Settings,
  ShieldCheck,
  Tags,
  LogOut,
  UserCog,
  FileText,
  Clock,
  Send,
  CircleDollarSign,
  ChevronDown,
  X,
} from "lucide-react";

function Sidebar({ isMobileOpen = false, onClose = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();

  /* =====================================================
     USER
  ===================================================== */
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    user = {};
  }

  /* -----------------------------------------------------
     TRAINER data (kept separate on purpose)
  ----------------------------------------------------- */
  let trainer = {};
  try {
    trainer = JSON.parse(
      localStorage.getItem("trainer") ||
        localStorage.getItem("trainerData") ||
        "{}"
    );
  } catch (error) {
    trainer = {};
  }

  const trainerToken = localStorage.getItem("trainerToken");
  const role = String(user?.role || "").trim().toUpperCase();

  /* =====================================================
     ROLE CHECK
  ===================================================== */
  const isAdmin = role === "ADMIN";
  const isTrainer =
    role === "TRAINER" ||
    Boolean(trainerToken) ||
    Boolean(trainer && (trainer.id || trainer._id || trainer.role));
  const isPartner = role === "PARTNER" || role === "RECRUITER";
  const isEmployee = role === "EMPLOYEE";

  /* =====================================================
     EMPLOYEE PAGE PERMISSIONS
     Stored by UserRoles.jsx in localStorage, keyed by email.
  ===================================================== */
  const [employeePermissions, setEmployeePermissions] = useState([]);

  useEffect(() => {
    if (!isEmployee) {
      setEmployeePermissions([]);
      return;
    }

    try {
      const permissionData = JSON.parse(
        localStorage.getItem("certed_employee_permissions") || "{}"
      );
      const email = String(user?.email || "").trim().toLowerCase();
      const savedPermissions = permissionData[email];

      setEmployeePermissions(
        Array.isArray(savedPermissions) ? savedPermissions : []
      );
    } catch (error) {
      console.error("Unable to load employee permissions:", error);
      setEmployeePermissions([]);
    }
  }, [isEmployee, user?.email]);

  const hasEmployeePermission = (path) => employeePermissions.includes(path);

  /* =====================================================
     USER NAME
  ===================================================== */
  const activeUser = isTrainer && (!user || !user.role) ? trainer : user;
  const userName =
    activeUser?.name || activeUser?.fullName || activeUser?.email || "User";
  const userInitial = String(userName).charAt(0).toUpperCase();

  /* =====================================================
     PARTNER COMPANY BRANDING
  ===================================================== */
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [partnerProfileApproved, setPartnerProfileApproved] = useState(false);

  const existingPartnerLogo =
    user?.logo || user?.companyLogo || user?.logoUrl || null;
  const partnerLogo = companyLogo || existingPartnerLogo || null;

  /* Load company logo + company name from Partner Profile data */
  useEffect(() => {
    if (!isPartner) return;

    let cancelled = false;

    const loadPartnerBranding = async () => {
      setPartnerProfileApproved(false);

      try {
        const API_BASE = "/api";
        const currentEmail = String(user?.email || "").trim().toLowerCase();

        if (!currentEmail) {
          console.warn("Partner email missing from logged-in user.");
          return;
        }

        // 1. Find the partner using the logged-in email.
        const partnersResponse = await fetch(`${API_BASE}/partners/all`);
        if (!partnersResponse.ok) {
          throw new Error("Unable to load partners");
        }

        const partnersData = await partnersResponse.json();
        const partnerList = Array.isArray(partnersData)
          ? partnersData
          : Array.isArray(partnersData?.data)
            ? partnersData.data
            : [];

        const currentPartner = partnerList.find((partner) => {
          const partnerEmail = String(
            partner?.email || partner?.user?.email || ""
          )
            .trim()
            .toLowerCase();
          return partnerEmail === currentEmail;
        });

        if (!currentPartner?.id) {
          console.warn("No partner record matched:", currentEmail);
          return;
        }

        // 2. Load company details using the partner table ID.
        const companyResponse = await fetch(
          `${API_BASE}/company-details/partner/${currentPartner.id}`
        );
        if (!companyResponse.ok) {
          throw new Error("Unable to load company details");
        }

        const companyDetailsResponse = await companyResponse.json();
        if (cancelled) return;

        const companyDetails = Array.isArray(companyDetailsResponse)
          ? companyDetailsResponse[0]
          : companyDetailsResponse?.data ?? companyDetailsResponse;

        // 3. Read each status from its correct field.
        const partnerStatus = String(currentPartner?.status || "")
          .trim()
          .toUpperCase();
        const companyStatus = String(companyDetails?.status || "")
          .trim()
          .toUpperCase();
        const verificationStatus = String(
          companyDetails?.verificationStatus ??
            companyDetails?.verification_status ??
            ""
        )
          .trim()
          .toUpperCase();

        console.log("PARTNER SIDEBAR STATUS CHECK:", {
          partnerId: currentPartner.id,
          email: currentEmail,
          partnerStatus,
          companyStatus,
          verificationStatus,
        });

        // Show partner modules only when all three statuses are correct.
        const isApproved =
          partnerStatus === "APPROVED" &&
          ["ACTIVE", "VERIFIED"].includes(companyStatus) &&
          verificationStatus === "VERIFIED";

        setPartnerProfileApproved(isApproved);

        // 4. Load company branding.
        const resolvedCompanyName =
          companyDetails?.companyDisplayName ||
          companyDetails?.company_display_name ||
          companyDetails?.companyName ||
          companyDetails?.company_name ||
          companyDetails?.companyLegalName ||
          companyDetails?.company_legal_name ||
          currentPartner?.companyName ||
          currentPartner?.company_name ||
          currentPartner?.name ||
          user?.companyName ||
          user?.company_name ||
          "Partner Portal";

        setCompanyName(resolvedCompanyName);

        const logoFileName =
          companyDetails?.logoFileName || companyDetails?.logo_file_name;

        if (logoFileName) {
          setCompanyLogo(
            `${API_BASE}/company-details/partner/${currentPartner.id}/logo?t=${Date.now()}`
          );
        } else {
          setCompanyLogo("");
        }
      } catch (error) {
        console.error("Partner branding load error:", error);
        if (!cancelled) {
          setPartnerProfileApproved(false);
          setCompanyName(
            user?.companyName || user?.company_name || "Partner Portal"
          );
          setCompanyLogo("");
        }
      }
    };

    loadPartnerBranding();

    return () => {
      cancelled = true;
    };
  }, [isPartner, user?.email, user?.companyName, user?.company_name]);

  /* =====================================================
     OPEN MENUS
  ===================================================== */
  const [openMenus, setOpenMenus] = useState({});

  /* Click-only toggle, no hover auto-open */
  const toggleMenu = (menuName) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  };

  /* =====================================================
     AUTO OPEN ACTIVE MENU
     Merged path map: both plain and "/admin/..." variants.
     Keys are unique across roles. Employee keys
     ("employee-<id>") are generated from the admin keys.
  ===================================================== */
  useEffect(() => {
    const path = location.pathname;

    const adminMenuPaths = {
      people: [
        "/employee-list",
        "/employee-register",
        "/trainer-list",
        "/trainer-working",
        "/admin/employee-list",
        "/admin/employee-register",
        "/admin/trainer-list",
        "/admin/trainer-working",
      ],
      partners: [
        "/partner-list",
        "/partner-leads",
        "/admin/recruiter-requests",
        "/admin/partner-list",
        "/admin/partner-leads",
      ],
      marketplace: [
        "/All-Jobs",
        "/training-opportunities",
        "/job-applications",
        "/job-matches",
        "/admin/jobs",
        "/admin/training-opportunities",
        "/admin/job-applications",
        "/admin/job-matches",
      ],
      adminTraining: [
        "/training-projects",
        "/training-requests",
        "/training-schedule",
        "/completed-trainings",
        "/admin/training-projects",
        "/admin/training-requests",
        "/admin/training-schedule",
        "/admin/completed-trainings",
      ],
      requests: [
        
        "/partner-job-requests",
        "/trainer-applications",
        "/pending-requests",
        "/approved-requests",
        "/rejected-requests",
        "/interview",
        "/admin/trainer-job-requests",
        "/admin/partner-job-requests",
        "/admin/trainer-applications",
        "/admin/pending-requests",
        "/admin/approved-requests",
        "/admin/rejected-requests",
      ],
      notifications: [
        "/job-notifications",
        "/trainer-notifications",
        "/partner-notifications",
        "/job-alerts",
        "/admin/job-notifications",
        "/admin/trainer-notifications",
        "/admin/partner-notifications",
        "/admin/job-alerts",
      ],
      subscriptionAdmin: [
        "/admin/subscription-management",
        "/admin/subscriptions",
        "/admin/subscription-plans",
        "/admin/subscription-products",
      ],
      adminPayments: [
        "/trainer-payments",
        "/partner-payments",
        "/pending-payments",
        "/payment-history",
        "/admin/trainer-payments",
        "/admin/partner-payments",
        "/admin/pending-payments",
        "/admin/payment-history",
      ],
      settings: [
        "/settings",
        "/admin/users-roles",
        "/admin/categories-skills",
        "/admin/settings",
      ],
    };

    // Employee versions of the admin menu groups
    const employeeMenuPaths = {};
    Object.keys(adminMenuPaths).forEach((key) => {
      employeeMenuPaths[`employee-${key}`] = adminMenuPaths[key];
    });

    const menuPaths = {
      ...adminMenuPaths,
      ...employeeMenuPaths,

      // Trainer
      profile: ["/trainer-profile", "/trainer-verification", "/trainer-skills"],
      trainerMarketplace: [
        "/trainer/available-jobs",
        "/trainer/my-application",
        "/trainer-job-matches",
      ],
      trainerTraining: [
        "/my-training-projects",
        "/my-training-schedule",
        "/completed-trainings",
      ],
      trainerRequests: ["/trainer-job-requests", "/trainer-applications"],
      trainerNotifications: ["/trainer-notifications", "/job-alerts"],
      trainerPayments: ["/trainer-payments", "/payment-history"],

      // Partner
      partnerProfileMenu: [
        "/partner-profile",
        "/partner-verification",
        "/partner/partner-profile",
        "/partner/partner-verification",
      ],
      partnerJobs: [
        "/partner/create-job",
        "/partner/partner-jobs",
        "/partner/application-job",
        "/partner/job-matches",
      ],
      partnerTrainers: [
        "/partner/search-trainers",
        "/partner/trainer-recently",
      ],
      partnerNotifications: [
        "/partner/partner-notifications",
        "/partner/job-alerts",
      ],
      subscriptionPartner: [
        "/partner/subscription",
        "/partner/subscription-plans",
      ],

      // Employee (own pages)
      employeeProfileMenu: ["/employee-profile"],
      employeeLeads: ["/employee-leads", "/lead-generation", "/follow-ups"],
      employeeTrainers: ["/trainer-list", "/trainer-working"],
      employeePartners: ["/partner-list", "/partner-leads"],
      employeeTraining: [
        "/training-projects",
        "/training-requests",
        "/training-schedule",
      ],
      admissions: ["/admissions", "/students", "/documents"],
      employeePayments: ["/payments", "/payment-history"],
      reports: ["/employee-reports"],
    };

    const newOpenMenus = {};

    Object.keys(menuPaths).forEach((menu) => {
      if (menuPaths[menu].includes(path)) {
        newOpenMenus[menu] = true;
      }
    });

    setOpenMenus((prev) => ({
      ...prev,
      ...newOpenMenus,
    }));
  }, [location.pathname]);

  /* =====================================================
     CLOSE MOBILE DRAWER ON ROUTE CHANGE
  ===================================================== */
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* =====================================================
     LOGOUT
  ===================================================== */
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("trainer");
    localStorage.removeItem("trainerData");
    localStorage.removeItem("trainerToken");

    navigate("/login");
  };

  /* =====================================================
     MENU SECTION
  ===================================================== */
  const MenuSection = ({ id, title, icon: Icon, children }) => {
    const isOpen = !!openMenus[id];

    return (
      <div className={`menu-wrapper ${isOpen ? "menu-open" : ""}`}>
        <button
          type="button"
          className="sidebar-link menu-parent"
          onClick={() => toggleMenu(id)}
          aria-expanded={isOpen}
        >
          <Icon className="sidebar-icon" />
          <span className="menu-parent-text">{title}</span>
          <ChevronDown
            size={16}
            className={`menu-chevron ${isOpen ? "rotate" : ""}`}
          />
        </button>

        <div className="sidebar-submenu">{children}</div>
      </div>
    );
  };

  /* =====================================================
     SUB LINK
  ===================================================== */
  const SubLink = ({ to, icon: Icon, children }) => {
    return (
      <NavLink to={to} className="sidebar-sub-link">
        <Icon size={15} />
        <span>{children}</span>
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile overlay — click to close */}
      {isMobileOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <aside className={`admin-sidebar ${isMobileOpen ? "mobile-open" : ""}`}>
        <style>{`

  * {
    box-sizing: border-box;
  }

  /* SIDEBAR */
  .admin-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: 250px;
    height: 100vh;
    background: #ffffff;
    color: #3E0F8D;
    display: flex;
    flex-direction: column;
    z-index: 9999;
    border-right: 1px solid #e5e0ea;
    box-shadow: 5px 0 24px rgba(62, 15, 141, 0.06);
    overflow: hidden;
    transition: transform 0.3s ease;
  }

  /* MOBILE OVERLAY */
  .sidebar-overlay {
    position: fixed;
    inset: 0;
    background: rgba(20, 10, 35, 0.45);
    z-index: 9998;
  }

  /* LOGO */
  .sidebar-logo {
    width: 100%;
    height: 72px;
    padding: 0 21px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    background: #ffffff;
    border-bottom: 1px solid #e9e5ed;
  }

  .sidebar-logo h2 {
    margin: 0;
    color: #3E0F8D;
    font-size: 23px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.4px;
  }

  .sidebar-logo h2 span {
    color: #9564DD;
  }

  .sidebar-logo small {
    display: block;
    margin-top: 6px;
    color: #8a8192;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.1px;
  }

  /* PARTNER COMPANY BRANDING */
  .sidebar-brand-with-company {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
    height: 58px;
    overflow: hidden;
  }

  .sidebar-company-logo-box {
    width: 48px;
    height: 48px;
    min-width: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    overflow: hidden;
    background: #f7f5fb;
    border: 1px solid #eee8f4;
  }

  .sidebar-company-logo {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
  }

  .sidebar-company-logo-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #3E0F8D;
    color: #ffffff;
    font-size: 20px;
    font-weight: 800;
  }

  .sidebar-company-info {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  .sidebar-company-name {
    color: #3E0F8D;
    font-size: 14px;
    font-weight: 800;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sidebar-portal-name {
    margin-top: 3px;
    color: #777080;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sidebar-logo-img {
    max-height: 40px;
    max-width: 190px;
    object-fit: contain;
  }

  .sidebar-close-btn {
    display: none;
    background: none;
    border: none;
    color: #5f5868;
    cursor: pointer;
    padding: 6px;
  }

  /* USER CARD */
  .sidebar-user {
    margin: 14px 12px 9px;
    padding: 11px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    background: linear-gradient(135deg, #f7f3fb, #f2ebfa);
    border: 1px solid #e8def3;
    border-radius: 12px;
    flex-shrink: 0;
  }

  .user-avatar {
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3E0F8D, #9564DD);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 800;
    color: #ffffff;
    box-shadow: 0 5px 12px rgba(62, 15, 141, 0.15);
  }

  .user-info {
    min-width: 0;
    overflow: hidden;
  }

  .user-name {
    font-size: 13px;
    font-weight: 800;
    color: #2f2440;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-role {
    margin-top: 4px;
    color: #9564DD;
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }

  /* MENU */
  .sidebar-menu {
    flex: 1;
    min-height: 0;
    padding: 7px 10px 14px;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: #d9c9eb transparent;
  }

  .sidebar-menu::-webkit-scrollbar {
    width: 4px;
  }

  .sidebar-menu::-webkit-scrollbar-track {
    background: transparent;
  }

  .sidebar-menu::-webkit-scrollbar-thumb {
    background: #d8c7ea;
    border-radius: 10px;
  }

  /* SECTION TITLE */
  .menu-title {
    padding: 15px 12px 7px;
    color: #8b8391;
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  /* MAIN LINK */
  .sidebar-link {
    width: 100%;
    min-height: 43px;
    padding: 10px 12px;
    margin-bottom: 3px;
    display: flex;
    align-items: center;
    gap: 11px;
    position: relative;
    border: none;
    border-radius: 9px;
    background: transparent;
    color: #5f5868;
    text-decoration: none;
    font-family: inherit;
    font-size: 13.5px;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease,
      box-shadow 0.2s ease;
  }

  .sidebar-link:hover {
    background: #f3edf9;
    color: #3E0F8D;
    transform: translateX(1px);
  }

  .sidebar-link.active {
    background: linear-gradient(90deg, #eee6f8, #f5f0fa);
    color: #3E0F8D;
    font-weight: 800;
    box-shadow: inset 4px 0 0 #3E0F8D;
  }

  .sidebar-link.active .sidebar-icon {
    color: #3E0F8D;
  }

  .sidebar-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: #766e80;
    transition: color 0.2s ease, transform 0.2s ease;
  }

  .sidebar-link:hover .sidebar-icon {
    color: #9564DD;
  }

  /* MENU WRAPPER / PARENT */
  .menu-wrapper {
    width: 100%;
  }

  .menu-parent {
    position: relative;
    appearance: none;
    outline: none;
  }

  .menu-parent-text {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .menu-wrapper.menu-open > .menu-parent {
    background: #f5f0fa;
    color: #3E0F8D;
    font-weight: 800;
  }

  .menu-wrapper.menu-open > .menu-parent .sidebar-icon {
    color: #9564DD;
  }

  .menu-chevron {
    margin-left: auto;
    flex-shrink: 0;
    color: #9a92a2;
    transition: transform 0.2s ease, color 0.2s ease;
  }

  .menu-parent:hover .menu-chevron {
    color: #9564DD;
  }

  .menu-chevron.rotate {
    transform: rotate(180deg);
    color: #3E0F8D;
  }

  /* SUBMENU — controlled ONLY by .menu-open (no hover auto-expand) */
  .sidebar-submenu {
    width: 100%;
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    padding-left: 11px;
    margin-left: 4px;
    border-left: 2px solid #eee6f6;
    transition: max-height 0.3s ease, opacity 0.2s ease, padding 0.2s ease;
  }

  .menu-wrapper.menu-open .sidebar-submenu {
    max-height: 700px;
    opacity: 1;
    padding-top: 4px;
    padding-bottom: 7px;
  }

  /* SUB LINK */
  .sidebar-sub-link {
    width: 100%;
    min-height: 37px;
    padding: 8px 10px;
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    gap: 9px;
    position: relative;
    border-radius: 7px;
    color: #777080;
    text-decoration: none;
    font-size: 12.5px;
    font-weight: 600;
    transition: background 0.2s ease, color 0.2s ease,
      padding-left 0.2s ease;
  }

  .sidebar-sub-link:hover {
    background: #f4eef9;
    color: #3E0F8D;
    padding-left: 14px;
    font-weight: 700;
  }

  .sidebar-sub-link.active {
    background: linear-gradient(90deg, #efe7f8, #f8f5fb);
    color: #3E0F8D;
    font-weight: 800;
    box-shadow: inset 3px 0 0 #9564DD;
  }

  .sidebar-sub-link svg {
    color: #9a91a4;
    flex-shrink: 0;
    transition: color 0.2s ease;
  }

  .sidebar-sub-link:hover svg,
  .sidebar-sub-link.active svg {
    color: #9564DD;
  }

  .sidebar-sub-link.active::after {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #E4DA72;
    position: absolute;
    right: 9px;
    top: 50%;
    transform: translateY(-50%);
  }

  /* FOOTER */
  .sidebar-footer {
    width: 100%;
    padding: 10px;
    flex-shrink: 0;
    background: #ffffff;
    border-top: 1px solid #e9e5ed;
  }

  .logout-btn {
    width: 100%;
    min-height: 43px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 11px;
    border: none;
    border-radius: 9px;
    background: #faf8fc;
    color: red;
    font-family: inherit;
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    text-align: left;
    transition: background 0.2s ease, color 0.2s ease;
  }

  .logout-btn:hover {
    background: #f6ece2;
  }

  .admin-sidebar::after {
    content: "";
    position: absolute;
    right: 0;
    top: 0;
    width: 1px;
    height: 100%;
    background: #EEEEEE;
    pointer-events: none;
  }

  /* TABLET */
  @media (max-width: 992px) {
    .admin-sidebar {
      width: 230px;
    }
  }

  /* MOBILE — slide-in drawer */
  @media (max-width: 768px) {
    .admin-sidebar {
      width: 260px;
      transform: translateX(-100%);
      box-shadow: none;
    }

    .admin-sidebar.mobile-open {
      transform: translateX(0);
      box-shadow: 5px 0 24px rgba(62, 15, 141, 0.18);
    }

    .sidebar-logo {
      padding: 0 16px;
    }

    .sidebar-close-btn {
      display: inline-flex;
    }

    .sidebar-user {
      margin: 10px 8px 8px;
    }

    .sidebar-menu {
      padding: 7px 8px 13px;
    }
  }

`}</style>

        {/* LOGO */}
        <div className="sidebar-logo">
          {isPartner ? (
            <div className="sidebar-brand-with-company">
              <div className="sidebar-company-logo-box">
                {partnerLogo ? (
                  <img
                    src={partnerLogo}
                    alt={companyName || "Company Logo"}
                    className="sidebar-company-logo"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="sidebar-company-logo-placeholder">
                    {String(companyName || "P").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              <div className="sidebar-company-info">
                <div className="sidebar-company-name">
                  {companyName || "Partner Portal"}
                </div>
                <div className="sidebar-portal-name">Partner Portal</div>
              </div>
            </div>
          ) : (
            <div>
              <h2>
                Cert<span>ED</span>
              </h2>
              <small>Freelance & Recruiter Marketplace</small>
            </div>
          )}

          <button
            type="button"
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* USER (hidden for now)
        <div className="sidebar-user">
          <div className="user-avatar">{userInitial}</div>
          <div className="user-info">
            <div className="user-name">{userName}</div>
            <div className="user-role">
              {isTrainer ? "TRAINER" : (role || "USER")}
            </div>
          </div>
        </div> */}

        {/* MENU */}
        <div className="sidebar-menu">
          {/* ADMIN */}
          {isAdmin && (
            <>
              <div className="menu-title">Main</div>

              <NavLink to="/dashboard" className="sidebar-link">
                <LayoutDashboard className="sidebar-icon" />
                <span>Dashboard</span>
              </NavLink>

              <div className="menu-title">People</div>

              <MenuSection id="people" title="People" icon={Users}>
                <SubLink to="/employee-list" icon={UserRound}>Employees</SubLink>
                <SubLink to="/employee-register" icon={UserPlus}>Add Employee</SubLink>
                <SubLink to="/trainer-list" icon={GraduationCap}>Trainers</SubLink>
                <SubLink to="/trainer-working" icon={UserCheck}>Trainers on Working</SubLink>
              </MenuSection>

              <div className="menu-title">Partners / Recruiters</div>

              <MenuSection id="partners" title="Partners / Recruiters" icon={Building2}>
                <SubLink to="/partner-list" icon={Building2}>All Partners</SubLink>
                <SubLink to="/partner-leads" icon={UserPlus}>Partner Leads</SubLink>
                <SubLink to="/admin/recruiter-requests" icon={Handshake}>Recruiter Requests</SubLink>
              </MenuSection>

              <div className="menu-title">Marketplace</div>

              <MenuSection id="marketplace" title="Marketplace" icon={BriefcaseBusiness}>
                <SubLink to="/All-Jobs" icon={BriefcaseBusiness}>All Jobs</SubLink>
                <SubLink to="/training-opportunities" icon={Search}>Training Opportunities</SubLink>
                <SubLink to="/job-applications" icon={ClipboardList}>Job Applications</SubLink>
                <SubLink to="/job-matches" icon={Handshake}>Job Matches</SubLink>
              </MenuSection>

              <div className="menu-title">Training</div>

              <MenuSection id="adminTraining" title="Training" icon={BookOpen}>
                <SubLink to="/training-projects" icon={BookOpen}>Training Projects</SubLink>
                <SubLink to="/training-requests" icon={CalendarCheck}>Training Requests</SubLink>
                <SubLink to="/training-schedule" icon={CalendarCheck}>Training Schedule</SubLink>
                <SubLink to="/completed-trainings" icon={CheckCircle}>Completed Trainings</SubLink>
              </MenuSection>

              <div className="menu-title">Requests</div>

              <MenuSection id="requests" title="Requests" icon={ClipboardList}>
                {/* <SubLink to="/trainer-job-requests" icon={UserPlus}>Trainer Job Requests</SubLink> */}
                <SubLink to="/partner-job-requests" icon={Building2}>Partner Job Requests</SubLink>
                <SubLink to="/interview" icon={ClipboardList}>Interview</SubLink>
               <SubLink to="/admin/pending-requests" icon={Clock}>
  Pending Requests
</SubLink>

<SubLink to="/admin/approved-requests" icon={CheckCircle}>
  Approved Requests
</SubLink>

<SubLink to="/admin/selected-requests" icon={UserCheck}>
  Final Selected Trainers
</SubLink>
              </MenuSection>

              <div className="menu-title">Notifications</div>

              <MenuSection id="notifications" title="Job Notifications" icon={Bell}>
                <SubLink to="/job-notifications" icon={Bell}>All Notifications</SubLink>
                <SubLink to="/trainer-notifications" icon={GraduationCap}>Trainer Notifications</SubLink>
                <SubLink to="/partner-notifications" icon={Building2}>Partner Notifications</SubLink>
                <SubLink to="/job-alerts" icon={Megaphone}>Job Alerts</SubLink>
              </MenuSection>

              <div className="menu-title">Subscriptions</div>

              <MenuSection id="subscriptionAdmin" title="Subscriptions" icon={CreditCard}>
                <SubLink to="/admin/subscription-management" icon={CreditCard}>All Subscriptions</SubLink>
                <SubLink to="/admin/subscription-plans" icon={Tags}>Subscription Plans</SubLink>
                <SubLink to="/admin/subscription-products" icon={Settings}>Subscription Products</SubLink>
              </MenuSection>

              <div className="menu-title">Finance</div>

              <MenuSection id="adminPayments" title="Payments" icon={Wallet}>
                <SubLink to="/trainer-payments" icon={GraduationCap}>Trainer Payments</SubLink>
                <SubLink to="/partner-payments" icon={Building2}>Partner Payments</SubLink>
                <SubLink to="/pending-payments" icon={CreditCard}>Pending Payments</SubLink>
                <SubLink to="/payment-history" icon={CreditCard}>Payment History</SubLink>
              </MenuSection>

              <div className="menu-title">Analytics</div>

              <NavLink to="/reports" className="sidebar-link">
                <BarChart3 className="sidebar-icon" />
                <span>Reports</span>
              </NavLink>

              <div className="menu-title">System</div>

              <MenuSection id="settings" title="Settings" icon={Settings}>
                <SubLink to="/admin/users-roles" icon={ShieldCheck}>Users & Roles</SubLink>
                <SubLink to="/admin/categories-skills" icon={Tags}>Categories & Skills</SubLink>
                <SubLink to="/settings" icon={Settings}>System Settings</SubLink>
              </MenuSection>
            </>
          )}

          {/* TRAINER */}
          {isTrainer && (
            <>
              <div className="menu-title">Trainer</div>

              <NavLink to="/trainer-dashboard" className="sidebar-link">
                <LayoutDashboard className="sidebar-icon" />
                <span>Dashboard</span>
              </NavLink>

              <MenuSection id="profile" title="My Profile" icon={UserRound}>
                <SubLink to="/trainer-profile" icon={UserRound}>My Profile</SubLink>
                <SubLink to="/trainer-verification" icon={ShieldCheck}>Verification</SubLink>
                <SubLink to="/trainer-skills" icon={Tags}>Skills & Expertise</SubLink>
              </MenuSection>

              <MenuSection id="trainerMarketplace" title="Marketplace" icon={BriefcaseBusiness}>
                <SubLink to="/trainer/available-jobs" icon={Search}>Available Jobs</SubLink>
                <SubLink to="/trainer/my-application" icon={ClipboardList}>My Applications</SubLink>
                <SubLink to="/trainer-job-matches" icon={Handshake}>Job Matches</SubLink>
              </MenuSection>

              <MenuSection id="trainerTraining" title="Training" icon={BookOpen}>
                <SubLink to="/my-training-projects" icon={BookOpen}>My Projects</SubLink>
                <SubLink to="/my-training-schedule" icon={CalendarCheck}>My Schedule</SubLink>
                <SubLink to="/completed-trainings" icon={CheckCircle}>Completed Trainings</SubLink>
              </MenuSection>

              <MenuSection id="trainerRequests" title="Requests" icon={ClipboardList}>
                <SubLink to="/trainer-job-requests" icon={Send}>Job Requests</SubLink>
                <SubLink to="/trainer-applications" icon={ClipboardList}>Applications</SubLink>
              </MenuSection>

              <MenuSection id="trainerNotifications" title="Notifications" icon={Bell}>
                <SubLink to="/trainer-notifications" icon={Bell}>Notifications</SubLink>
                <SubLink to="/job-alerts" icon={Megaphone}>Job Alerts</SubLink>
              </MenuSection>

              <MenuSection id="trainerPayments" title="Payments" icon={Wallet}>
                <SubLink to="/trainer-payments" icon={Wallet}>My Payments</SubLink>
                <SubLink to="/payment-history" icon={CreditCard}>Payment History</SubLink>
              </MenuSection>
            </>
          )}

          {/* PARTNER / RECRUITER */}
          {isPartner && (
            <>
              <div className="menu-title">Partner / Recruiter</div>

              {/* Dashboard appears only after approval */}
              {partnerProfileApproved && (
                <NavLink to="/partner-dashboard" className="sidebar-link">
                  <LayoutDashboard className="sidebar-icon" />
                  <span>Dashboard</span>
                </NavLink>
              )}

              {/* My Profile is always accessible */}
              <MenuSection id="partnerProfileMenu" title="My Profile" icon={Building2}>
                <SubLink to="/partner/partner-profile" icon={Building2}>Company Profile</SubLink>
                <SubLink to="/partner/partner-verification" icon={ShieldCheck}>Verification</SubLink>
              </MenuSection>

              {/* Other modules appear only when approved */}
              {partnerProfileApproved && (
                <>
                  <MenuSection id="partnerJobs" title="Jobs" icon={BriefcaseBusiness}>
                    <SubLink to="/partner/create-job" icon={UserPlus}>Post New Job</SubLink>
                    <SubLink to="/partner/partner-jobs" icon={BriefcaseBusiness}>My Jobs</SubLink>
                    <SubLink to="/partner/application-job" icon={ClipboardList}>Applications</SubLink>
                    <SubLink to="/partner/job-matches" icon={Handshake}>Job Matches</SubLink>
                  </MenuSection>

                  <MenuSection id="partnerTrainers" title="Trainers" icon={GraduationCap}>
                    <SubLink to="/partner/search-trainers" icon={Search}>Search Trainers</SubLink>
                    <SubLink to="/partner/trainer-recently" icon={UserPlus}>Recently Search</SubLink>
                  </MenuSection>

                  <MenuSection id="partnerNotifications" title="Notifications" icon={Bell}>
                    <SubLink to="/partner/partner-notifications" icon={Bell}>Notifications</SubLink>
                    <SubLink to="/partner/job-alerts" icon={Megaphone}>Job Alerts</SubLink>
                  </MenuSection>

                  <div className="menu-title">Subscription</div>

                  <MenuSection id="subscriptionPartner" title="Subscription" icon={CreditCard}>
                    <SubLink to="/partner/subscription" icon={CreditCard}>My Subscription</SubLink>
                    <SubLink to="/partner/subscription-plans" icon={Tags}>Subscription Plans</SubLink>
                  </MenuSection>
                </>
              )}
            </>
          )}

          {/* EMPLOYEE — ADMIN MENU WITH ADMIN-GRANTED PAGE PERMISSIONS */}
          {isEmployee && (
            <>
              <div className="menu-title">Main</div>

              {/* Employee home remains available to the logged-in employee. */}
              <NavLink to="/employee-dashboard" className="sidebar-link">
                <LayoutDashboard className="sidebar-icon" />
                <span>Dashboard</span>
              </NavLink>

              {/* Admin dashboard appears only if explicitly granted. */}
              {hasEmployeePermission("/dashboard") && (
                <NavLink to="/dashboard" className="sidebar-link">
                  <LayoutDashboard className="sidebar-icon" />
                  <span>Admin Dashboard</span>
                </NavLink>
              )}

              {[
                {
                  id: "people",
                  title: "People",
                  icon: Users,
                  items: [
                    { label: "Employees", path: "/employee-list", icon: UserRound },
                    { label: "Add Employee", path: "/employee-register", icon: UserPlus },
                    { label: "Trainers", path: "/trainer-list", icon: GraduationCap },
                    { label: "Trainers on Working", path: "/trainer-working", icon: UserCheck },
                  ],
                },
                {
                  id: "partners",
                  title: "Partners / Recruiters",
                  icon: Building2,
                  items: [
                    { label: "All Partners", path: "/partner-list", icon: Building2 },
                    { label: "Partner Leads", path: "/partner-leads", icon: UserPlus },
                    { label: "Recruiter Requests", path: "/admin/recruiter-requests", icon: Handshake },
                  ],
                },
                {
                  id: "marketplace",
                  title: "Marketplace",
                  icon: BriefcaseBusiness,
                  items: [
                    { label: "All Jobs", path: "/All-Jobs", icon: BriefcaseBusiness },
                    { label: "Training Opportunities", path: "/training-opportunities", icon: Search },
                    { label: "Job Applications", path: "/job-applications", icon: ClipboardList },
                    { label: "Job Matches", path: "/job-matches", icon: Handshake },
                  ],
                },
                {
                  id: "adminTraining",
                  title: "Training",
                  icon: BookOpen,
                  items: [
                    { label: "Training Projects", path: "/training-projects", icon: BookOpen },
                    { label: "Training Requests", path: "/training-requests", icon: CalendarCheck },
                    { label: "Training Schedule", path: "/training-schedule", icon: CalendarCheck },
                    { label: "Completed Trainings", path: "/completed-trainings", icon: CheckCircle },
                  ],
                },
                {
                  id: "requests",
                  title: "Requests",
                  icon: ClipboardList,
                  items: [
                    { label: "Trainer Job Requests", path: "/trainer-job-requests", icon: UserPlus },
                    { label: "Partner Job Requests", path: "/partner-job-requests", icon: Building2 },
                    { label: "Trainer Applications", path: "/trainer-applications", icon: ClipboardList },
                    { label: "Pending Requests", path: "/pending-requests", icon: Clock },
                    { label: "Approved Requests", path: "/approved-requests", icon: CheckCircle },
                    { label: "Rejected Requests", path: "/rejected-requests", icon: X },
                  ],
                },
                {
                  id: "notifications",
                  title: "Job Notifications",
                  icon: Bell,
                  items: [
                    { label: "All Notifications", path: "/job-notifications", icon: Bell },
                    { label: "Trainer Notifications", path: "/trainer-notifications", icon: GraduationCap },
                    { label: "Partner Notifications", path: "/partner-notifications", icon: Building2 },
                    { label: "Job Alerts", path: "/job-alerts", icon: Megaphone },
                  ],
                },
                {
                  id: "subscriptionAdmin",
                  title: "Subscriptions",
                  icon: CreditCard,
                  items: [
                    { label: "All Subscriptions", path: "/admin/subscription-management", icon: CreditCard },
                    { label: "Subscription Plans", path: "/admin/subscription-plans", icon: Tags },
                    { label: "Subscription Products", path: "/admin/subscription-products", icon: Settings },
                  ],
                },
                {
                  id: "adminPayments",
                  title: "Payments",
                  icon: Wallet,
                  items: [
                    { label: "Trainer Payments", path: "/trainer-payments", icon: GraduationCap },
                    { label: "Partner Payments", path: "/partner-payments", icon: Building2 },
                    { label: "Pending Payments", path: "/pending-payments", icon: CreditCard },
                    { label: "Payment History", path: "/payment-history", icon: CreditCard },
                  ],
                },
                {
                  id: "settings",
                  title: "Settings",
                  icon: Settings,
                  items: [
                    { label: "Users & Roles", path: "/admin/users-roles", icon: ShieldCheck },
                    { label: "Categories & Skills", path: "/admin/categories-skills", icon: Tags },
                    { label: "System Settings", path: "/settings", icon: Settings },
                  ],
                },
              ].map((menu) => {
                const allowedItems = menu.items.filter((item) =>
                  hasEmployeePermission(item.path)
                );

                if (allowedItems.length === 0) return null;
                const MenuIcon = menu.icon;

                return (
                  <React.Fragment key={`employee-${menu.id}`}>
                    <div className="menu-title">{menu.title}</div>
                    <MenuSection
                      id={`employee-${menu.id}`}
                      title={menu.title}
                      icon={MenuIcon}
                    >
                      {allowedItems.map((item) => {
                        const ItemIcon = item.icon;
                        return (
                          <SubLink key={item.path} to={item.path} icon={ItemIcon}>
                            {item.label}
                          </SubLink>
                        );
                      })}
                    </MenuSection>
                  </React.Fragment>
                );
              })}

              {hasEmployeePermission("/reports") && (
                <>
                  <div className="menu-title">Analytics</div>
                  <NavLink to="/reports" className="sidebar-link">
                    <BarChart3 className="sidebar-icon" />
                    <span>Reports</span>
                  </NavLink>
                </>
              )}

              {/* Keep the employee's own profile available. */}
              <NavLink to="/employee-profile" className="sidebar-link">
                <UserCog className="sidebar-icon" />
                <span>My Profile</span>
              </NavLink>
            </>
          )}

          {/* UNKNOWN ROLE */}
          {!isAdmin && !isTrainer && !isPartner && !isEmployee && (
            <>
              <div className="menu-title">Main</div>
              <NavLink to="/dashboard" className="sidebar-link">
                <LayoutDashboard className="sidebar-icon" />
                <span>Dashboard</span>
              </NavLink>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="sidebar-footer">
          <button type="button" className="logout-btn" onClick={logout}>
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;